import { css } from '@emotion/react';
import React, { useCallback, useEffect, useState } from 'react';
import t from 'react-translate';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import {
  CheckInUser,
} from 'redux/schemas/api/in-person-event';
import { useAppDispatch } from 'redux/store';
import { RootState } from 'redux/schemas';
import {
  getInPersonEventConfirmations,
  getInPersonEventCheckInQr,
  inPersonEventCheckIn,
} from 'redux/actions/in-person-events';
import { AttendeeInfoStatus } from 'redux/schemas/models/in-person-event';

import { standardSpacing, halfSpacing } from 'styles/global_defaults/scaffolding';
import { primary } from 'styles/global_defaults/colors';
import { semiBoldFontWeight } from 'styles/global_defaults/fonts';
import { handheld, notHandheld } from 'styles/global_defaults/media-queries';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import LoadingWrapper, { LoaderType } from 'shared/components/loading-wrapper';
import { NvSearchBar } from 'shared/components/nv-search-bar';
import NvUserAvatar from 'components/nv-user-avatar';
import NvIcon from 'shared/components/nv-icon';

const styles = css`
  max-width: 600px;
  .search-bar{
    .clear-button {
      color: ${primary};
    }
  }
  .result-row {
    gap: ${standardSpacing}px;
  }
  .user-info{
    gap: ${halfSpacing}px;
    flex: 0 0 40%;
  }
  .qr-image{
    display: block;
    margin: 0 auto;
    max-width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
    ${handheld(css`
      width: min(335px, 100%);
    `)};
    ${notHandheld(css`
      width: min(500px, 100%);
    `)}
  }
  .clear-button {
    .text-small {
      color: ${primary} !important;
      font-weight: ${semiBoldFontWeight};
    }
  }
`;

type InPersonEventCheckInModalProps = {
  catalogId: string;
  sessionId: number;
  show: boolean;
  onClose: () => void;
};

const InPersonEventCheckInModal = ({
  catalogId,
  sessionId,
  show,
  onClose,
}: InPersonEventCheckInModalProps) => {
  const qrCodeUrl = useSelector((state: RootState) => state.models.inPersonEventSessions[sessionId]?.qrCodeUrl);
  const [isQrLoading, setIsQrLoading] = useState(false);
  const [isQrLoaded, setIsQrLoaded] = useState(!!qrCodeUrl);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CheckInUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [checkingIn, setCheckingIn] = useState<Record<number, boolean>>({});
  const [checkedInUsers, setCheckedInUsers] = useState<number[]>([]);

  const dispatch = useAppDispatch();

  const fetchQrCode = useCallback(async () => {
    setIsQrLoading(true);
    setIsQrLoaded(true);
    try {
      await dispatch(getInPersonEventCheckInQr({ catalogId, sessionId })).unwrap();
    } catch {
      setIsQrLoaded(false);
    } finally {
      setIsQrLoading(false);
    }
  }, [catalogId, dispatch, sessionId]);

  useEffect(() => {
    if (show && !qrCodeUrl && !isQrLoading && !isQrLoaded) {
      fetchQrCode();
    }
  }, [show, qrCodeUrl, isQrLoading, isQrLoaded, fetchQrCode]);

  useEffect(() => {
    if (!show) {
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [show]);

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    setIsSearching(true);
    try {
      const result: CheckInUser[] = await dispatch(
        getInPersonEventConfirmations({ catalogId, sessionId, searchBy: query }),
      ).unwrap();
      const checkedInUserIds = result
        .filter(user => (
          user.status === AttendeeInfoStatus.CHECK_IN_MANUAL
        || user.status === AttendeeInfoStatus.CHECK_IN_QR_CODE
        || user.status === AttendeeInfoStatus.COMPLETED))
        .map(user => user.userId);
      setCheckedInUsers(checkedInUserIds);
      setSearchResults(result);
    } catch {
      // Error alert handled by action
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const userCheckIn = async (userId: number) => {
    setCheckingIn(prev => ({ ...prev, [userId]: true }));
    try {
      await dispatch(inPersonEventCheckIn({
        catalogId, sessionId, userId,
      })).unwrap();
      setCheckedInUsers(prev => [...prev, userId]);
    } catch {
      // Error alert handled by action
    } finally {
      setCheckingIn(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setSearchResults([]);
  };

  const body = (
    <div
      className='d-flex flex-column align-items-center mx-auto py-7'
      css={styles}
    >
      <div>
        <div className='text-center page-title-small'>
          {t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.CHECK_IN_QR_DESCRIPTION_1()}
        </div>
        <div className='text-center page-title-small'>
          {t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.CHECK_IN_QR_DESCRIPTION_2()}
        </div>
      </div>
      <div className='w-100 border-bottom border-gray-4 pb-2 mt-4'>
        <NvSearchBar
          placeholder={t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.CHECK_IN_SEARCH_PLACEHOLDER()}
          onSearch={handleSearch}
          clearButtonEnabled
          autoHideClearbutton
          onClearButton={handleClear}
          className='search-bar w-100'
        />
      </div>
      {searchQuery ? (
        <LoadingWrapper
          loaderType={LoaderType.PLACEHOLDER}
          isLoaded={!isSearching}
          className='w-100 mt-4'
        >
          <div className='w-100 mt-4'>
            {searchResults.length === 0 && !isSearching ? (
              <div className='d-flex align-items-center'>
                <NvIcon icon='warning' size='small' className='text-danger mr-2' />
                <span className='text-regular'>
                  {t.SEARCH.NO_RESULTS_FOUND()}
                </span>
              </div>
            ) : (
              searchResults.map((user) => (
                <div
                  key={user.id}
                  className='result-row d-flex align-items-center py-3 border-bottom border-gray-6'
                >
                  <div
                    className='user-info d-flex flex-row align-items-center'
                  >
                    <NvUserAvatar
                      borderType='round'
                      size='lg'
                      user={user}
                      tooltipEnabled={false}
                      className='mb-0'
                    />
                    <div className='text-regular bold'>
                      {`${user.firstName} ${user.lastName}`}
                    </div>
                  </div>
                  <div className='flex-grow-1 text-regular'>
                    {user.email}
                  </div>
                  {checkedInUsers?.includes(user.userId) ? (
                    <div className='text-success text-regular font-weight-bold d-flex align-items-center'>
                      <NvIcon icon='check' size='xss-smallest' className='mr-2' />
                      {t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.CHECKED_IN()}
                    </div>
                  ) : (
                    <Button
                      variant='primary'
                      onClick={() => userCheckIn(user.userId)}
                      disabled={checkingIn[user.userId]}
                      className='text-nowrap'
                    >
                      {checkingIn[user.userId]
                        ? t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.CHECKING_IN()
                        : t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.CHECK_IN_BUTTON()}
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </LoadingWrapper>
      ) : (
        <LoadingWrapper
          loaderType={LoaderType.PLACEHOLDER}
          isLoaded={!isQrLoading}
          className='w-100 mt-4'
        >
          {qrCodeUrl && (
            <img
              src={qrCodeUrl}
              alt={t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.CHECK_IN_QR()}
              className='qr-image mt-4'
            />
          )}
        </LoadingWrapper>
      )}
    </div>
  );

  return (
    <NvModal
      show={show}
      type={ModalType.DYNAMIC}
      header={t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.LEARNER_CHECK_IN()}
      body={body}
      onClose={onClose}
      fullHeight={false}
    />
  );
};

export default InPersonEventCheckInModal;
